import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CONF = {
  high: { label: "High", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "SignalHigh" },
  medium: { label: "Medium", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: "SignalMedium" },
  low: { label: "Low", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: "SignalLow" },
};
const CAT_ICON = { "U.S. Individual": "User", "Business & International": "Building2", "Thai Services": "Globe" };

function RecCard({ eid, rec, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const c = CONF[rec.confidence] || CONF.low;
  const CI = Icons[CAT_ICON[rec.category]] || Icons.Sparkles;

  const act = async (verb, body) => {
    setBusy(true);
    try {
      await api.post(`/service-intelligence/engagements/${eid}/recommendations/${rec.id}/${verb}`, body || {});
      toast.success(verb === "accept" ? `Added ${rec.service_name} to the engagement` : verb === "dismiss" ? "Recommendation dismissed" : "Recommendation restored");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); setDismissing(false); }
  };

  const decided = rec.status === "accepted" || rec.status === "dismissed";
  return (
    <div className={cn("rounded-xl border p-3.5 bg-white", decided ? "border-slate-100 opacity-80" : "border-slate-200")}
         data-testid={`svc-rec-${rec.service_code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-[#171B2E]/5 flex items-center justify-center shrink-0"><CI className="h-4 w-4 text-[#171B2E]" /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[#171B2E] text-sm">{rec.service_name}</span>
              <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full border font-medium", c.cls)}>{c.label} confidence</span>
              {rec.status === "accepted" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Accepted</span>}
              {rec.status === "dismissed" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">Dismissed</span>}
              {rec.already_on_engagement && rec.status !== "accepted" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">Already on engagement</span>}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">{rec.category} · {rec.form_reference}</div>
          </div>
        </div>
        {!decided && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => act("accept")} disabled={busy} data-testid={`svc-rec-accept-${rec.service_code}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg bg-[#171B2E] text-white text-xs font-semibold hover:bg-[#171B2E]/90 disabled:opacity-50">
              <Icons.Check className="h-3.5 w-3.5" />Accept
            </button>
            <button onClick={() => setDismissing((v) => !v)} disabled={busy} data-testid={`svc-rec-dismiss-${rec.service_code}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg border border-slate-200 text-slate-500 text-xs hover:bg-slate-50">
              <Icons.X className="h-3.5 w-3.5" />Dismiss
            </button>
          </div>
        )}
        {decided && (
          <button onClick={() => act("reset")} disabled={busy} className="text-[11px] text-slate-400 hover:text-slate-600 underline shrink-0">Undo</button>
        )}
      </div>

      {/* WHY */}
      <div className="mt-2.5 pl-[42px] space-y-1.5">
        <div className="flex gap-1.5 text-xs text-slate-600">
          <Icons.Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
          <span>{rec.why}</span>
        </div>
        {/* EVIDENCE */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Evidence:</span>
          {(rec.evidence || []).map((e, i) => (
            <span key={i} title={e.source} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-600">
              <Icons.FileCheck className="h-3 w-3 text-slate-400" />{e.label}{e.detail && e.detail !== e.label ? `: ${e.detail}` : ""}
            </span>
          ))}
        </div>
      </div>

      {dismissing && (
        <div className="mt-2.5 flex items-center gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason (recommended)"
                 data-testid={`svc-rec-dismiss-reason-${rec.service_code}`}
                 className="flex-1 h-8 px-2.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-slate-400" />
          <button onClick={() => act("dismiss", { reason })} disabled={busy} className="h-8 px-3 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700">Confirm dismiss</button>
        </div>
      )}
      {rec.status === "dismissed" && rec.dismiss_reason && (
        <div className="mt-1.5 pl-[42px] text-[11px] text-slate-400 italic">Reason: {rec.dismiss_reason}</div>
      )}
    </div>
  );
}

export function ServiceRecommendations({ engagementId }) {
  const [data, setData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [open, setOpen] = useState(true);

  const load = useCallback(() => api.get(`/service-intelligence/engagements/${engagementId}/recommendations`)
    .then((r) => setData(r.data)).catch((e) => setData(e.response?.status === 404 ? { recommendations: [] } : false)), [engagementId]);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    try { const r = await api.post(`/service-intelligence/engagements/${engagementId}/analyze`); setData(r.data); toast.success("Allison re-analyzed this engagement"); }
    catch { toast.error("Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  if (data === false) return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid="service-recommendations-error">
      <span className="text-sm text-slate-500 inline-flex items-center gap-2"><Icons.AlertCircle className="h-4 w-4 text-amber-500" />Could not load service recommendations.</span>
      <button onClick={load} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Icons.RefreshCw className="h-3.5 w-3.5" />Retry</button>
    </div>
  );
  const recs = data?.recommendations || [];
  const pending = recs.filter((r) => r.status === "recommended");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="service-recommendations">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#171B2E] to-[#2A3050]">
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-white">
          <Icons.Sparkles className="h-4 w-4 text-amber-300" />
          <span className="font-heading font-semibold text-sm">Allison — Service Recommendations</span>
          {pending.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-300 text-[#171B2E] font-bold">{pending.length} to review</span>}
          <Icons.ChevronDown className={cn("h-4 w-4 transition-transform", !open && "-rotate-90")} />
        </button>
        <button onClick={analyze} disabled={analyzing} data-testid="svc-rec-analyze"
                className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs">
          {analyzing ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.RefreshCw className="h-3.5 w-3.5" />}Re-analyze
        </button>
      </div>
      {open && (
        <div className="p-4 space-y-2.5">
          {(data?.client_requested || []).length > 0 && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="svc-rec-client-requested">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icons.UserCheck className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Client requested</span>
              </div>
              <ul className="space-y-1">
                {data.client_requested.map((r, i) => (
                  <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5" data-testid={`svc-rec-requested-${i}`}>
                    <span className="text-emerald-500 mt-0.5">•</span>
                    <span>{r.service_name}{r.year_applicability === "specific_years" && r.years?.length ? ` — ${r.years[0]}${r.years.length > 1 ? `\u2013${r.years[r.years.length - 1]}` : ""}` : (r.year_applicability === "not_applicable" ? " — Not year-specific" : "")}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-emerald-700/70 mt-1.5">Client intent — not yet recommended, priced, or added. Review below.</p>
            </div>
          )}
          {data === null ? (
            <div className="py-6 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>
          ) : recs.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm" data-testid="svc-rec-empty">
              <Icons.SearchX className="h-7 w-7 mx-auto mb-1.5 text-slate-300" />
              No service recommendations yet — Allison makes no recommendation without supporting evidence.
            </div>
          ) : (
            <>
              <p className="text-[11px] text-slate-400 -mt-1">Evidence-based recommendations. Nothing is added to the engagement until you accept it.</p>
              {recs.map((r) => <RecCard key={r.id} eid={engagementId} rec={r} onChanged={load} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
